import { useMemo, useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { motion } from "framer-motion";
import { AlertTriangle, Printer, RotateCcw } from "lucide-react";

import { CalculatorLayout } from "@/components/calculator/CalculatorLayout";
import { NumberInput } from "@/components/calculator/NumberInput";
import { ShareButton } from "@/components/calculator/ShareButton";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { TooltipProvider } from "@/components/ui/tooltip";

import { useAppStore } from "@/lib/store";
import { toBengaliNumerals } from "@/lib/bdNumerals";
import { formatBDT } from "@/lib/bdCurrency";
import { buildSeo } from "@/lib/seo";
import { cn } from "@/lib/utils";

import { computeTax, getTaxYear, getExemption } from "@/lib/calculators/tax";
import {
  TAX_YEARS,
  CATEGORY_LABEL,
  LOCATION_LABEL,
  type AssesseeCategory,
  type Location,
} from "@/data/bdTaxSlabs";

type Bilingual = { en: string; bn: string };

const STR = {
  h1: { en: "Bangladesh Income Tax Calculator", bn: "বাংলাদেশ আয়কর ক্যালকুলেটর" },
  desc: {
    en: "Calculate Bangladesh income tax with current NBR slabs, investment rebate, minimum tax, and surcharge for any assessee category. Indicative — verify with NBR before filing.",
    bn: "এনবিআর-এর বর্তমান স্ল্যাব, ইনভেস্টমেন্ট রিবেট, মিনিমাম ট্যাক্স ও সারচার্জ সহ যেকোনো শ্রেণির করদাতার আয়কর হিসাব করুন। আনুমানিক — ফাইলিংয়ের আগে এনবিআর থেকে যাচাই করুন।",
  },
  taxYear: { en: "Tax year", bn: "কর বছর" },
  category: { en: "Assessee category", bn: "করদাতার শ্রেণি" },
  location: { en: "Location (for minimum tax)", bn: "অবস্থান (মিনিমাম ট্যাক্সের জন্য)" },

  incomeTitle: { en: "Annual income", bn: "বার্ষিক আয়" },
  salary: { en: "Salary income", bn: "বেতন আয়" },
  interest: { en: "Interest on savings (DPS, FDR, Sanchayapatra above limits)", bn: "সঞ্চয়ের সুদ (ডিপিএস, এফডিআর, সঞ্চয়পত্র সীমার বেশি)" },
  rent: { en: "House property rent (after deductions)", bn: "বাড়ি ভাড়া (ছাড়ের পর)" },
  business: { en: "Business / profession income", bn: "ব্যবসা / পেশার আয়" },
  capital: { en: "Capital gains", bn: "মূলধনী লাভ" },
  other: { en: "Other sources", bn: "অন্যান্য উৎস" },

  deductionsTitle: { en: "Deductions & rebate", bn: "ছাড় ও রিবেট" },
  exempt: { en: "Tax-exempt salary components (HRA, medical, conveyance — capped)", bn: "করমুক্ত বেতন উপাদান (এইচআরএ, মেডিকেল, কনভেয়েন্স — সীমাবদ্ধ)" },
  investment: { en: "Investment for rebate (DPS, life insurance, mutual fund, Sanchayapatra)", bn: "রিবেটের জন্য বিনিয়োগ (ডিপিএস, লাইফ ইন্স্যুরেন্স, মিউচুয়াল ফান্ড, সঞ্চয়পত্র)" },
  pf: { en: "Provident Fund contribution", bn: "প্রভিডেন্ট ফান্ড অবদান" },

  netWealth: { en: "Net wealth (for surcharge — leave 0 if below ৳4 crore)", bn: "নিট সম্পদ (সারচার্জের জন্য — ৪ কোটির নিচে হলে ০ রাখুন)" },
  tds: { en: "TDS already deducted", bn: "ইতিমধ্যে কাটা টিডিএস" },

  totalIncome: { en: "Total income", bn: "মোট আয়" },
  taxableIncome: { en: "Taxable income", bn: "করযোগ্য আয়" },
  taxPayable: { en: "Final tax", bn: "চূড়ান্ত কর" },
  effectiveRate: { en: "Effective tax rate", bn: "কার্যকর করের হার" },
  payableDue: { en: "Additional payment due", bn: "অতিরিক্ত পরিশোধযোগ্য" },
  refund: { en: "Refund expected", bn: "প্রত্যাশিত ফেরত" },

  slabBreakdown: { en: "Slab-wise breakdown", bn: "স্ল্যাবভিত্তিক বিবরণ" },
  slab: { en: "Slab", bn: "স্ল্যাব" },
  range: { en: "Range", bn: "পরিধি" },
  rate: { en: "Rate", bn: "হার" },
  tax: { en: "Tax", bn: "কর" },

  grossTax: { en: "Gross tax (from slabs)", bn: "মোট কর (স্ল্যাব থেকে)" },
  rebate: { en: "Investment rebate (15%)", bn: "ইনভেস্টমেন্ট রিবেট (১৫%)" },
  minTax: { en: "Minimum tax", bn: "মিনিমাম ট্যাক্স" },
  surcharge: { en: "Surcharge", bn: "সারচার্জ" },
  tdsPaid: { en: "TDS already paid", bn: "ইতিমধ্যে পরিশোধিত টিডিএস" },

  reset: { en: "Reset", bn: "রিসেট" },
  bnDigits: { en: "Bengali numerals", bn: "বাংলা সংখ্যা" },
  print: { en: "Print / PDF", bn: "প্রিন্ট / PDF" },

  disclaimer: {
    en: "This calculator is for indicative purposes only. Tax depends on your specific circumstances. Consult a registered tax practitioner or refer to NBR (nbr.gov.bd) before filing.",
    bn: "এই ক্যালকুলেটর শুধু আনুমানিক হিসাবের জন্য। কর আপনার নির্দিষ্ট পরিস্থিতির উপর নির্ভর করে। ফাইলিংয়ের আগে নিবন্ধিত কর-পরামর্শক বা এনবিআর (nbr.gov.bd) দেখুন।",
  },
  lastUpdated: { en: "Last updated", bn: "সর্বশেষ হালনাগাদ" },
} as const;

const FAQ: { q: Bilingual; a: Bilingual }[] = [
  {
    q: { en: "How is income tax calculated in Bangladesh?", bn: "ট্যাক্স কীভাবে গণনা করা হয়?" },
    a: {
      en: "Add all annual income, subtract exempt portions, apply progressive slabs above your category exemption (৳3.5L for general male, ৳4L for women/seniors, ৳4.75L for disabled, ৳5L for wounded freedom fighters). Then subtract investment rebate (15%), apply minimum tax floor if applicable, and add surcharge if net wealth exceeds ৳4 crore.",
      bn: "সব বার্ষিক আয় যোগ করুন, করমুক্ত অংশ বাদ দিন, আপনার শ্রেণির করমুক্ত সীমার (সাধারণ পুরুষ ৳৩.৫ লাখ, মহিলা/জ্যেষ্ঠ ৳৪ লাখ, প্রতিবন্ধী ৳৪.৭৫ লাখ, আহত মুক্তিযোদ্ধা ৳৫ লাখ) উপর প্রগতিশীল স্ল্যাব প্রয়োগ করুন। তারপর ১৫% ইনভেস্টমেন্ট রিবেট বাদ দিন, প্রযোজ্য হলে মিনিমাম ট্যাক্স প্রয়োগ করুন এবং নিট সম্পদ ৪ কোটির বেশি হলে সারচার্জ যোগ করুন।",
    },
  },
  {
    q: { en: "What is investment rebate?", bn: "ইনভেস্টমেন্ট রিবেট কী?" },
    a: {
      en: "Section 78 allows a 15% rebate on qualifying investments (DPS, life insurance premium, Sanchayapatra, mutual fund units, recognised provident fund, etc.). The rebate base is capped at the lower of: 20% of taxable income, your actual investment, or ৳1.5 crore.",
      bn: "ধারা ৭৮ অনুযায়ী যোগ্য বিনিয়োগে (ডিপিএস, লাইফ ইন্স্যুরেন্স প্রিমিয়াম, সঞ্চয়পত্র, মিউচুয়াল ফান্ড, স্বীকৃত প্রভিডেন্ট ফান্ড ইত্যাদি) ১৫% রিবেট পাওয়া যায়। রিবেটের ভিত্তি হলো এই তিনটির সর্বনিম্ন: করযোগ্য আয়ের ২০%, প্রকৃত বিনিয়োগ, বা ৳১.৫ কোটি।",
    },
  },
  {
    q: { en: "What is minimum tax?", bn: "মিনিমাম ট্যাক্স কী?" },
    a: {
      en: "If your computed tax (after rebate) is less than the minimum, you must still pay the minimum: ৳5,000 in Dhaka/Chittagong City Corp., ৳4,000 in other city corporations, ৳3,000 elsewhere. It only applies when you have taxable income above the exemption threshold.",
      bn: "রিবেটের পর গণনাকৃত কর মিনিমামের কম হলেও মিনিমাম পরিশোধ করতে হয়: ঢাকা/চট্টগ্রাম সিটিতে ৳৫,০০০, অন্যান্য সিটিতে ৳৪,০০০, অন্যত্র ৳৩,০০০। শুধু করমুক্ত সীমার উপরে আয় থাকলে প্রযোজ্য।",
    },
  },
  {
    q: { en: "What is TDS?", bn: "টিডিএস কী?" },
    a: {
      en: "TDS (Tax Deducted at Source) is tax withheld by the payer (employer, bank, tenant, etc.) and deposited with the government on your behalf. It's a prepayment — your final tax for the year is offset against TDS already paid; if TDS exceeds final tax, you can claim a refund.",
      bn: "টিডিএস (Tax Deducted at Source) হলো উৎসে কর কর্তন — আপনার পক্ষে নিয়োগকর্তা, ব্যাংক, ভাড়াটিয়া ইত্যাদি কেটে সরকারে জমা দেয়। এটি অগ্রিম পরিশোধ; বছরের চূড়ান্ত কর থেকে এই টিডিএস বাদ যায়, বেশি দিলে ফেরত নেওয়া যায়।",
    },
  },
  {
    q: { en: "Who must file an income tax return in Bangladesh?", bn: "বাংলাদেশে কাদের আয়কর রিটার্ন দিতে হয়?" },
    a: {
      en: "Anyone with taxable income above the exemption threshold, all TIN holders, and 40+ specified categories (govt employees, business owners, those with bank balance > ৳10L, car owners, club members, etc.) must file — regardless of taxable income.",
      bn: "করমুক্ত সীমার বেশি আয় থাকলে, সব টিআইএনধারী, এবং ৪০+ নির্দিষ্ট শ্রেণির ব্যক্তিকে (সরকারি কর্মচারী, ব্যবসায়ী, ব্যাংকে ১০ লাখের বেশি ব্যালান্স থাকলে, গাড়ির মালিক, ক্লাব সদস্য ইত্যাদি) — আয় যাই হোক — রিটার্ন দাখিল করতে হয়।",
    },
  },
  {
    q: { en: "When is the deadline to file?", bn: "রিটার্ন দাখিলের শেষ তারিখ কখন?" },
    a: {
      en: "The standard deadline (\"Tax Day\") is 30 November of the assessment year — i.e. the year after the fiscal year ends. NBR sometimes extends it. File via the eReturn portal at etaxnbr.gov.bd or in person at your tax circle.",
      bn: "সাধারণ শেষ তারিখ (\"ট্যাক্স ডে\") অর্থবছর শেষের পরবর্তী বছরের ৩০ নভেম্বর। কখনো এনবিআর সময় বাড়ায়। etaxnbr.gov.bd-এ অনলাইনে বা আপনার কর সার্কেলে দাখিল করুন।",
    },
  },
  {
    q: { en: "What is the penalty for late filing?", bn: "দেরিতে দাখিলের জরিমানা কী?" },
    a: {
      en: "Up to 10% of the last assessed tax (minimum ৳1,000), plus ৳50/day for continued default. Interest at 2% per month is also charged on unpaid tax. Late filing also disqualifies you from many tax-deducted-at-source rebates.",
      bn: "সর্বশেষ নির্ধারিত করের ১০% পর্যন্ত (সর্বনিম্ন ৳১,০০০), এর সঙ্গে দৈনিক ৳৫০ অব্যাহত জরিমানা। অপরিশোধিত করে মাসিক ২% সুদ। দেরি দাখিল অনেক উৎসে কর কর্তন রিবেট থেকেও বঞ্চিত করে।",
    },
  },
  {
    q: { en: "How do I get a TIN in Bangladesh?", bn: "বাংলাদেশে টিআইএন কীভাবে পাব?" },
    a: {
      en: "Register online at secure.incometax.gov.bd with your NID, mobile and email. The eTIN certificate is issued instantly and free. You'll need it to open a bank account >৳10L, register a vehicle, get a trade licence, and many other transactions.",
      bn: "secure.incometax.gov.bd-এ এনআইডি, মোবাইল ও ইমেইল দিয়ে অনলাইনে নিবন্ধন করুন। ই-টিআইএন সার্টিফিকেট সঙ্গে সঙ্গে বিনামূল্যে পাওয়া যায়। ১০ লাখের বেশি ব্যাংক হিসাব খুলতে, গাড়ি নিবন্ধন, ট্রেড লাইসেন্স ও অনেক লেনদেনে এটি লাগে।",
    },
  },
  {
    q: { en: "Are HRA, medical and conveyance allowances tax-free?", bn: "এইচআরএ, মেডিকেল ও কনভেয়েন্স ভাতা কি করমুক্ত?" },
    a: {
      en: "Partially. House Rent Allowance is exempt up to 50% of basic salary or ৳3,00,000 per year (whichever is lower). Medical allowance is exempt up to 10% of basic or ৳1,20,000. Conveyance up to ৳30,000. Anything above is taxable.",
      bn: "আংশিকভাবে। বাড়িভাড়া ভাতা মূল বেতনের ৫০% বা বছরে ৳৩,০০,০০০ যেটি কম, ততটুকু করমুক্ত। মেডিকেল মূল বেতনের ১০% বা ৳১,২০,০০০। কনভেয়েন্স ৳৩০,০০০ পর্যন্ত। এর বেশি করযোগ্য।",
    },
  },
  {
    q: { en: "What is the surcharge on high net worth?", bn: "উচ্চ সম্পদে সারচার্জ কী?" },
    a: {
      en: "If your net wealth exceeds ৳4 crore, an additional surcharge applies on your income tax: 10% (>৳4cr), 20% (>৳10cr), 30% (>৳20cr), 35% (>৳50cr). Net wealth = total assets − total liabilities, declared in your annual statement of affairs.",
      bn: "নিট সম্পদ ৪ কোটির বেশি হলে আয়করের উপর অতিরিক্ত সারচার্জ: ১০% (>৪ কোটি), ২০% (>১০ কোটি), ৩০% (>২০ কোটি), ৩৫% (>৫০ কোটি)। নিট সম্পদ = মোট সম্পদ − মোট দায়, বার্ষিক সম্পদ বিবরণীতে ঘোষণা করতে হয়।",
    },
  },
  {
    q: { en: "Is freelancing income taxable in Bangladesh?", bn: "ফ্রিল্যান্সিং আয় কি বাংলাদেশে করযোগ্য?" },
    a: {
      en: "Yes, freelancing income is treated as business income and added to your total. However, IT/ITES exports remitted through banks are currently tax-exempt under specific conditions until 30 June 2027 — verify the latest SRO at NBR.",
      bn: "হ্যাঁ, ফ্রিল্যান্সিং আয় ব্যবসায় আয় হিসেবে গণ্য হয় ও মোটে যোগ হয়। তবে ব্যাংকের মাধ্যমে আনা আইটি/আইটিইএস রপ্তানি আয় নির্দিষ্ট শর্তে ৩০ জুন ২০২৭ পর্যন্ত করমুক্ত — সর্বশেষ এসআরও এনবিআরে যাচাই করুন।",
    },
  },
  {
    q: { en: "Does this calculator handle wealth statement / asset disclosure?", bn: "এই ক্যালকুলেটর কি সম্পদ বিবরণী হ্যান্ডেল করে?" },
    a: {
      en: "No. It only calculates income tax. Statement of Assets, Liabilities and Lifestyle (IT-10B/IT-10BB) is a separate filing requirement — the eReturn portal walks you through it after you submit income.",
      bn: "না। এটি শুধু আয়কর হিসাব করে। সম্পদ, দায় ও জীবনযাত্রার বিবরণী (আইটি-১০বি/আইটি-১০বিবি) আলাদা দাখিল — আয় জমা দেওয়ার পর ই-রিটার্ন পোর্টাল এটি গাইড করে।",
    },
  },
];

export const Route = createFileRoute("/tax-calculator")({
  head: () => {
    const seo = buildSeo({
      title: "Income Tax Calculator Bangladesh FY 2025-26",
      description:
        "Free Bangladesh income tax calculator with current NBR slabs. Calculate income tax for salaried, business, with investment rebate. ইনকাম ট্যাক্স ক্যালকুলেটর বাংলাদেশ।",
      path: "/tax-calculator",
      breadcrumbs: [
        { name: "Home", path: "/" },
        { name: "Income Tax Calculator", path: "/tax-calculator" },
      ],
      faq: FAQ.map((f) => ({ q: f.q.en, a: f.a.en })),
    });
    return { meta: seo.meta, links: seo.links, scripts: seo.scripts };
  },
  component: TaxCalculatorPage,
});

function TaxCalculatorPage() {
  const lang = useAppStore((s) => s.lang);
  const tr = (k: keyof typeof STR) => (STR[k] as Bilingual)[lang];
  const num = (n: number | string) => (lang === "bn" ? toBengaliNumerals(n) : String(n));

  const [yearId, setYearId] = useState<string>(TAX_YEARS[0].id);
  const [category, setCategory] = useState<AssesseeCategory>("male");
  const [location, setLocation] = useState<Location>("dhaka_chittagong");

  // Income sources
  const [salary, setSalary] = useState("1000000");
  const [interest, setInterest] = useState("0");
  const [rent, setRent] = useState("0");
  const [business, setBusiness] = useState("0");
  const [capital, setCapital] = useState("0");
  const [other, setOther] = useState("0");

  // Deductions
  const [exempt, setExempt] = useState("0");
  const [investment, setInvestment] = useState("100000");
  const [pf, setPf] = useState("0");

  const [netWealth, setNetWealth] = useState("0");
  const [tds, setTds] = useState("0");
  const [bnDigits, setBnDigits] = useState(false);

  const totalIncome =
    n(salary) + n(interest) + n(rent) + n(business) + n(capital) + n(other);
  const totalExempt = n(exempt) + n(pf);
  const taxableIncome = Math.max(0, totalIncome - totalExempt);
  const totalInvestment = n(investment) + n(pf);

  const result = useMemo(
    () =>
      computeTax({
        taxYearId: yearId,
        category,
        location,
        taxableIncome,
        investment: totalInvestment,
        netWealth: n(netWealth),
        tdsPaid: n(tds),
      }),
    [yearId, category, location, taxableIncome, totalInvestment, netWealth, tds],
  );

  const year = getTaxYear(yearId);
  const exemption = getExemption(year, category);

  const money = (v: number, frac = 0) => {
    const useBn = lang === "bn" || bnDigits;
    return formatBDT(Math.round(v * 100) / 100, useBn ? "bn" : "en", frac);
  };

  const reset = () => {
    setYearId(TAX_YEARS[0].id);
    setCategory("male");
    setLocation("dhaka_chittagong");
    setSalary("1000000");
    setInterest("0");
    setRent("0");
    setBusiness("0");
    setCapital("0");
    setOther("0");
    setExempt("0");
    setInvestment("100000");
    setPf("0");
    setNetWealth("0");
    setTds("0");
  };

  const refundDue = result.payableOrRefund;

  return (
    <CalculatorLayout
      slug="tax-calculator"
      title={tr("h1")}
      description={tr("desc")}
      iconName="Receipt"
      faq={FAQ.map((f) => ({ q: f.q[lang], a: f.a[lang] }))}
    >
      <TooltipProvider delayDuration={150}>
        {/* Disclaimer (top) */}
        <Disclaimer lang={lang} year={year} />

        {/* Year + category + location */}
        <div className="mt-5 grid gap-4 md:grid-cols-3">
          <Field label={tr("taxYear")} lang={lang}>
            <Select value={yearId} onValueChange={setYearId}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                {TAX_YEARS.map((y) => (
                  <SelectItem key={y.id} value={y.id} className={lang === "bn" ? "font-bn" : ""}>
                    {y.fyLabel[lang]} ({y.ayLabel[lang]})
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </Field>

          <Field label={tr("category")} lang={lang} hint={`${money(exemption)} ${lang === "en" ? "exempt" : "করমুক্ত"}`}>
            <Select value={category} onValueChange={(v) => setCategory(v as AssesseeCategory)}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                {(Object.keys(CATEGORY_LABEL) as AssesseeCategory[]).map((c) => (
                  <SelectItem key={c} value={c} className={lang === "bn" ? "font-bn" : ""}>
                    {CATEGORY_LABEL[c][lang]}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </Field>

          <Field label={tr("location")} lang={lang}>
            <Select value={location} onValueChange={(v) => setLocation(v as Location)}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                {(Object.keys(LOCATION_LABEL) as Location[]).map((l) => (
                  <SelectItem key={l} value={l} className={lang === "bn" ? "font-bn" : ""}>
                    {LOCATION_LABEL[l][lang]}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </Field>
        </div>

        {/* Income */}
        <SectionHeader title={tr("incomeTitle")} lang={lang} />
        <div className="grid gap-4 md:grid-cols-2">
          <NumberInput label={tr("salary")} value={salary} onChange={setSalary} unit="৳" />
          <NumberInput label={tr("interest")} value={interest} onChange={setInterest} unit="৳" />
          <NumberInput label={tr("rent")} value={rent} onChange={setRent} unit="৳" />
          <NumberInput label={tr("business")} value={business} onChange={setBusiness} unit="৳" />
          <NumberInput label={tr("capital")} value={capital} onChange={setCapital} unit="৳" />
          <NumberInput label={tr("other")} value={other} onChange={setOther} unit="৳" />
        </div>
        <div className="mt-3 rounded-lg border border-border bg-muted/40 px-4 py-2 text-sm">
          <span className={cn("text-muted-foreground", lang === "bn" && "font-bn")}>{tr("totalIncome")}: </span>
          <span className="font-semibold tabular-nums">{money(totalIncome)}</span>
        </div>

        {/* Deductions */}
        <SectionHeader title={tr("deductionsTitle")} lang={lang} />
        <div className="grid gap-4 md:grid-cols-2">
          <NumberInput label={tr("exempt")} value={exempt} onChange={setExempt} unit="৳" />
          <NumberInput label={tr("investment")} value={investment} onChange={setInvestment} unit="৳" />
          <NumberInput label={tr("pf")} value={pf} onChange={setPf} unit="৳" />
          <NumberInput label={tr("netWealth")} value={netWealth} onChange={setNetWealth} unit="৳" />
        </div>

        <div className="mt-4">
          <NumberInput label={tr("tds")} value={tds} onChange={setTds} unit="৳" />
        </div>

        <div className="mt-6 flex items-center justify-between gap-3">
          <label className={cn("inline-flex items-center gap-2 text-xs text-muted-foreground", lang === "bn" && "font-bn")}>
            <input
              type="checkbox"
              checked={bnDigits}
              onChange={(e) => setBnDigits(e.target.checked)}
              className="h-3.5 w-3.5 accent-accent"
            />
            {tr("bnDigits")}
          </label>
          <Button variant="outline" onClick={reset} className="gap-2">
            <RotateCcw size={14} />
            {tr("reset")}
          </Button>
        </div>

        {/* Results */}
        <motion.div
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.35 }}
          className="mt-8 space-y-6"
        >
          <div className="grid gap-3 md:grid-cols-4">
            <ResultCard label={tr("totalIncome")} value={money(totalIncome)} />
            <ResultCard label={tr("taxableIncome")} value={money(taxableIncome)} />
            <ResultCard label={tr("taxPayable")} value={money(result.finalTax)} accent />
            <ResultCard
              label={tr("effectiveRate")}
              value={`${num((result.effectiveRate * 100).toFixed(2))}%`}
            />
          </div>

          {/* Slab breakdown */}
          <div className="rounded-xl border border-border bg-card p-3 md:p-4">
            <h3 className={cn("mb-3 text-sm font-semibold", lang === "bn" && "font-bn")}>{tr("slabBreakdown")}</h3>
            <div className="overflow-x-auto rounded-md border border-border">
              <table className="w-full text-sm">
                <thead className="bg-muted/60 text-xs uppercase tracking-wide text-muted-foreground">
                  <tr>
                    <th className={cn("px-3 py-2 text-left", lang === "bn" && "font-bn")}>{tr("slab")}</th>
                    <th className={cn("px-3 py-2 text-left", lang === "bn" && "font-bn")}>{tr("range")}</th>
                    <th className={cn("px-3 py-2 text-right", lang === "bn" && "font-bn")}>{tr("rate")}</th>
                    <th className={cn("px-3 py-2 text-right", lang === "bn" && "font-bn")}>{tr("tax")}</th>
                  </tr>
                </thead>
                <tbody>
                  {result.slabs.map((s) => {
                    const used = s.taxableInThisSlab > 0;
                    const width = isFinite(s.rangeEnd) ? s.rangeEnd - s.rangeStart : Math.max(s.taxableInThisSlab, 1);
                    const fill = width > 0 ? Math.min(100, (s.taxableInThisSlab / width) * 100) : 0;
                    return (
                      <tr key={s.index} className={cn("border-t border-border", !used && "text-muted-foreground/60")}>
                        <td className="px-3 py-2 tabular-nums">{num(s.index)}</td>
                        <td className="px-3 py-2 tabular-nums">
                          <div>
                            {money(s.rangeStart)} – {isFinite(s.rangeEnd) ? money(s.rangeEnd) : (lang === "en" ? "above" : "তদূর্ধ্ব")}
                          </div>
                          <div className="mt-1 h-1 w-full max-w-[180px] overflow-hidden rounded-full bg-muted">
                            <div className="h-full bg-accent" style={{ width: `${fill}%` }} />
                          </div>
                        </td>
                        <td className="px-3 py-2 text-right tabular-nums">{num((s.rate * 100).toFixed(0))}%</td>
                        <td className="px-3 py-2 text-right tabular-nums">{money(s.taxFromThisSlab)}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>

          {/* Reconciliation */}
          <div className="rounded-xl border border-border bg-card p-4">
            <dl className="space-y-2 text-sm">
              <Row label={tr("grossTax")} value={money(result.grossTax)} lang={lang} />
              <Row label={tr("rebate")} value={`− ${money(result.rebate)}`} lang={lang} muted />
              <Row label={lang === "en" ? "Tax after rebate" : "রিবেট পরবর্তী কর"} value={money(result.taxAfterRebate)} lang={lang} divider />
              {result.minimumTaxApplied && (
                <Row
                  label={`${tr("minTax")} (${LOCATION_LABEL[location][lang]})`}
                  value={money(result.minimumTax)}
                  lang={lang}
                  warn
                />
              )}
              {result.surchargeRate > 0 && (
                <Row
                  label={`${tr("surcharge")} (${num((result.surchargeRate * 100).toFixed(0))}%)`}
                  value={`+ ${money(result.surcharge)}`}
                  lang={lang}
                />
              )}
              <Row label={tr("taxPayable")} value={money(result.finalTax)} lang={lang} divider strong />
              <Row label={tr("tdsPaid")} value={`− ${money(result.tdsPaid)}`} lang={lang} muted />
              <Row
                label={refundDue >= 0 ? tr("payableDue") : tr("refund")}
                value={money(Math.abs(refundDue))}
                lang={lang}
                strong
                accent={refundDue < 0 ? "good" : "bad"}
              />
            </dl>
            <div className="mt-4 flex flex-wrap gap-2">
              <Button size="sm" variant="outline" onClick={() => window.print()} className="gap-1.5">
                <Printer size={14} />
                {tr("print")}
              </Button>
              <ShareButton title={`${tr("taxPayable")}: ${money(result.finalTax)}`} />
            </div>
          </div>
        </motion.div>

        {/* Disclaimer (bottom) */}
        <div className="mt-8">
          <Disclaimer lang={lang} year={year} />
        </div>

        {/* Educational content */}
        <article className="mt-12 space-y-10">
          <Section title={lang === "en" ? "How income tax works in Bangladesh" : "বাংলাদেশে আয়কর কীভাবে কাজ করে"} lang={lang}>
            <p className={cn("text-sm text-muted-foreground", lang === "bn" && "font-bn")}>
              {lang === "en"
                ? "Income tax in Bangladesh is administered by the National Board of Revenue (NBR). Every resident assessee with taxable income above the exemption threshold — and many specified categories regardless of income — must file an annual return for the fiscal year (1 July – 30 June). Tax is computed on total income from all sources, after subtracting exempt portions, then progressive slab rates are applied to the amount above your category exemption."
                : "বাংলাদেশে আয়কর জাতীয় রাজস্ব বোর্ড (এনবিআর) পরিচালনা করে। করমুক্ত সীমার বেশি করযোগ্য আয়ের সব আবাসিক করদাতা — এবং নির্দিষ্ট শ্রেণির ব্যক্তিদের আয় যাই হোক — অর্থবছরের (১ জুলাই – ৩০ জুন) জন্য বার্ষিক রিটার্ন দাখিল করতে হয়। সব উৎসের মোট আয় থেকে করমুক্ত অংশ বাদ দিয়ে শ্রেণির করমুক্ত সীমার উপরে প্রগতিশীল স্ল্যাব হারে কর গণনা হয়।"}
            </p>
          </Section>

          <Section title={`${lang === "en" ? "Tax slabs" : "কর স্ল্যাব"} — ${year.fyLabel[lang]}`} lang={lang}>
            <SlabReferenceTable lang={lang} year={year} />
          </Section>

          <Section title={lang === "en" ? "Tax-free allowance limits" : "করমুক্ত ভাতা সীমা"} lang={lang}>
            <ul className={cn("list-disc space-y-1.5 pl-5 text-sm text-muted-foreground", lang === "bn" && "font-bn")}>
              {lang === "en" ? (
                <>
                  <li>House Rent Allowance (HRA): exempt up to <strong>50% of basic salary or ৳3,00,000/year</strong>, whichever is lower.</li>
                  <li>Medical allowance: exempt up to <strong>10% of basic or ৳1,20,000/year</strong>, whichever is lower (higher caps for disabled employees).</li>
                  <li>Conveyance allowance: exempt up to <strong>৳30,000/year</strong>.</li>
                  <li>Festival bonus: <strong>up to 2 months' basic</strong> per year is allowed before tax.</li>
                </>
              ) : (
                <>
                  <li>বাড়িভাড়া ভাতা (এইচআরএ): মূল বেতনের <strong>৫০% বা বছরে ৳৩,০০,০০০</strong> যা কম, ততটুকু করমুক্ত।</li>
                  <li>মেডিকেল ভাতা: মূল বেতনের <strong>১০% বা বছরে ৳১,২০,০০০</strong> যা কম, ততটুকু করমুক্ত (প্রতিবন্ধীদের জন্য উচ্চতর সীমা)।</li>
                  <li>কনভেয়েন্স ভাতা: বছরে <strong>৳৩০,০০০</strong> পর্যন্ত করমুক্ত।</li>
                  <li>উৎসব বোনাস: বছরে <strong>২ মাসের মূল বেতন পর্যন্ত</strong> করমুক্ত।</li>
                </>
              )}
            </ul>
          </Section>

          <Section title={lang === "en" ? "Investment rebate (Section 78)" : "ইনভেস্টমেন্ট রিবেট (ধারা ৭৮)"} lang={lang}>
            <p className={cn("text-sm text-muted-foreground", lang === "bn" && "font-bn")}>
              {lang === "en"
                ? "Qualifying investments include: DPS contributions (up to ৳1.2L/year), life insurance premium, Sanchayapatra purchases, mutual fund and listed-share purchases, recognised provident fund and approved superannuation fund. Rebate = 15% × min(your investment, 20% of taxable income, ৳1.5 crore)."
                : "যোগ্য বিনিয়োগ: ডিপিএস (বছরে ৳১.২ লাখ পর্যন্ত), লাইফ ইন্স্যুরেন্স প্রিমিয়াম, সঞ্চয়পত্র, মিউচুয়াল ফান্ড ও তালিকাভুক্ত শেয়ার ক্রয়, স্বীকৃত প্রভিডেন্ট ফান্ড ও অনুমোদিত সুপারঅ্যানুয়েশন ফান্ড। রিবেট = ১৫% × min(আপনার বিনিয়োগ, করযোগ্য আয়ের ২০%, ৳১.৫ কোটি)।"}
            </p>
          </Section>

          <Section title={lang === "en" ? "Minimum tax" : "মিনিমাম ট্যাক্স"} lang={lang}>
            <p className={cn("text-sm text-muted-foreground", lang === "bn" && "font-bn")}>
              {lang === "en"
                ? "Once you have any taxable income above your exemption threshold, you must pay at least the minimum tax — even if rebate brings the calculated tax below it. The minimum is ৳5,000 for residents of Dhaka and Chittagong City Corporations, ৳4,000 for other city corporations, and ৳3,000 elsewhere."
                : "করমুক্ত সীমার বেশি কোনো করযোগ্য আয় থাকলেই অন্তত মিনিমাম ট্যাক্স দিতে হয় — রিবেটের পর গণনাকৃত কর কম হলেও। ঢাকা ও চট্টগ্রাম সিটিতে ৳৫,০০০, অন্যান্য সিটিতে ৳৪,০০০, অন্যত্র ৳৩,০০০।"}
            </p>
          </Section>

          <Section title={lang === "en" ? "Surcharge on high net worth" : "উচ্চ নিট সম্পদে সারচার্জ"} lang={lang}>
            <ul className={cn("list-disc space-y-1 pl-5 text-sm text-muted-foreground", lang === "bn" && "font-bn")}>
              <li>{lang === "en" ? "Net wealth > ৳4 crore: 10% surcharge on income tax" : "নিট সম্পদ > ৪ কোটি: আয়করের উপর ১০% সারচার্জ"}</li>
              <li>{lang === "en" ? "> ৳10 crore: 20%" : "> ১০ কোটি: ২০%"}</li>
              <li>{lang === "en" ? "> ৳20 crore: 30%" : "> ২০ কোটি: ৩০%"}</li>
              <li>{lang === "en" ? "> ৳50 crore: 35%" : "> ৫০ কোটি: ৩৫%"}</li>
            </ul>
          </Section>

          <Section title={lang === "en" ? "TDS — what to deduct from your tax" : "টিডিএস — কর থেকে যা বাদ যাবে"} lang={lang}>
            <p className={cn("text-sm text-muted-foreground", lang === "bn" && "font-bn")}>
              {lang === "en"
                ? "Your employer, bank (on FDR/DPS interest), tenant (on house rent above ৳25,000/month), and any AIT-deducting payer issues a TDS certificate. Sum these up and enter under \"TDS already deducted\" — your final tax will reduce by this amount, and any excess can be claimed as refund."
                : "আপনার নিয়োগকর্তা, ব্যাংক (এফডিআর/ডিপিএস সুদে), ভাড়াটিয়া (মাসে ৳২৫,০০০-এর বেশি বাড়িভাড়ায়), এবং যে কোনো এআইটি কর্তনকারী টিডিএস সার্টিফিকেট দেয়। এগুলো যোগ করে \"ইতিমধ্যে কাটা টিডিএস\" ঘরে দিন — চূড়ান্ত কর থেকে এটা বাদ যাবে, বেশি দিলে ফেরত দাবি করা যাবে।"}
            </p>
          </Section>

          <Section title={lang === "en" ? "How to file your return" : "রিটার্ন কীভাবে দাখিল করবেন"} lang={lang}>
            <ol className={cn("list-decimal space-y-1 pl-5 text-sm text-muted-foreground", lang === "bn" && "font-bn")}>
              {lang === "en" ? (
                <>
                  <li>Get a TIN from secure.incometax.gov.bd if you don't have one.</li>
                  <li>Sign in to the eReturn portal at etaxnbr.gov.bd.</li>
                  <li>Enter income, deductions, investment, TDS — system computes tax.</li>
                  <li>Submit IT-10B/IT-10BB (statement of assets, liabilities and lifestyle).</li>
                  <li>Pay any balance via online banking, mobile financial services, or bank challan.</li>
                  <li>Download the acknowledgement (PSR) — you'll need it for many transactions.</li>
                </>
              ) : (
                <>
                  <li>secure.incometax.gov.bd থেকে টিআইএন নিন (না থাকলে)।</li>
                  <li>etaxnbr.gov.bd-এ ই-রিটার্ন পোর্টালে সাইন ইন করুন।</li>
                  <li>আয়, ছাড়, বিনিয়োগ, টিডিএস দিন — সিস্টেম কর গণনা করবে।</li>
                  <li>আইটি-১০বি/আইটি-১০বিবি (সম্পদ, দায় ও জীবনযাত্রার বিবরণী) দাখিল করুন।</li>
                  <li>বাকি কর অনলাইন ব্যাংকিং, মোবাইল ব্যাংকিং বা ব্যাংক চালান-এ পরিশোধ করুন।</li>
                  <li>প্রাপ্তি স্বীকার (PSR) ডাউনলোড করুন — অনেক লেনদেনে লাগে।</li>
                </>
              )}
            </ol>
            <p className={cn("mt-3 text-xs text-muted-foreground", lang === "bn" && "font-bn")}>
              {lang === "en"
                ? <>Official: <a href="https://nbr.gov.bd" target="_blank" rel="noopener noreferrer" className="text-accent hover:underline">nbr.gov.bd</a> · eReturn: <a href="https://etaxnbr.gov.bd" target="_blank" rel="noopener noreferrer" className="text-accent hover:underline">etaxnbr.gov.bd</a></>
                : <>সরকারি: <a href="https://nbr.gov.bd" target="_blank" rel="noopener noreferrer" className="text-accent hover:underline">nbr.gov.bd</a> · ই-রিটার্ন: <a href="https://etaxnbr.gov.bd" target="_blank" rel="noopener noreferrer" className="text-accent hover:underline">etaxnbr.gov.bd</a></>}
            </p>
          </Section>
        </article>
      </TooltipProvider>
    </CalculatorLayout>
  );
}

/* ---------- Helpers ---------- */

function n(s: string): number {
  const v = parseFloat(s);
  return Number.isFinite(v) && v > 0 ? v : 0;
}

function Disclaimer({ lang, year }: { lang: "en" | "bn"; year: ReturnType<typeof getTaxYear> }) {
  return (
    <div className="rounded-xl border border-yellow-500/30 bg-yellow-500/5 p-4">
      <div className="flex items-start gap-2">
        <AlertTriangle size={16} className="mt-0.5 shrink-0 text-yellow-600 dark:text-yellow-400" />
        <div>
          <p className={cn("text-sm font-medium text-yellow-700 dark:text-yellow-300", lang === "bn" && "font-bn")}>
            {(STR.disclaimer as Bilingual)[lang]}
          </p>
          <p className={cn("mt-1 text-xs text-muted-foreground", lang === "bn" && "font-bn")}>
            <strong>{(STR.lastUpdated as Bilingual)[lang]}:</strong> {year.lastUpdated} · {year.source[lang]}
          </p>
        </div>
      </div>
    </div>
  );
}

function Field({ label, lang, hint, children }: { label: string; lang: "en" | "bn"; hint?: string; children: React.ReactNode }) {
  return (
    <div className="space-y-1.5">
      <label className={cn("flex items-center justify-between text-sm font-medium", lang === "bn" && "font-bn")}>
        <span>{label}</span>
        {hint && <span className="text-[11px] font-normal text-accent">{hint}</span>}
      </label>
      {children}
    </div>
  );
}

function SectionHeader({ title, lang }: { title: string; lang: "en" | "bn" }) {
  return (
    <h2 className={cn("mt-8 mb-3 text-sm font-semibold uppercase tracking-wide text-muted-foreground", lang === "bn" && "font-bn")}>
      {title}
    </h2>
  );
}

function ResultCard({ label, value, accent }: { label: string; value: string; accent?: boolean }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 6 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
      className={cn(
        "rounded-xl border p-4",
        accent ? "border-accent/40 bg-accent/5" : "border-border bg-card",
      )}
    >
      <p className={cn("text-xs font-semibold uppercase tracking-wide", accent ? "text-accent" : "text-muted-foreground")}>
        {label}
      </p>
      <p className="mt-1 text-xl font-semibold tabular-nums md:text-2xl">{value}</p>
    </motion.div>
  );
}

function Row({
  label,
  value,
  lang,
  muted,
  divider,
  strong,
  warn,
  accent,
}: {
  label: string;
  value: string;
  lang: "en" | "bn";
  muted?: boolean;
  divider?: boolean;
  strong?: boolean;
  warn?: boolean;
  accent?: "good" | "bad";
}) {
  return (
    <div
      className={cn(
        "flex items-center justify-between gap-3",
        divider && "border-t border-border pt-2",
      )}
    >
      <dt className={cn("text-sm", lang === "bn" && "font-bn", muted && "text-muted-foreground", warn && "text-yellow-600 dark:text-yellow-400")}>
        {label}
      </dt>
      <dd
        className={cn(
          "tabular-nums text-sm font-medium",
          strong && "text-base font-semibold",
          accent === "good" && "text-emerald-600 dark:text-emerald-400",
          accent === "bad" && "text-rose-600 dark:text-rose-400",
        )}
      >
        {value}
      </dd>
    </div>
  );
}

function Section({ title, children, lang }: { title: string; children: React.ReactNode; lang: "en" | "bn" }) {
  return (
    <section>
      <h2 className={cn("mb-3 text-lg font-bold tracking-tight", lang === "bn" && "font-bn")}>{title}</h2>
      {children}
    </section>
  );
}

function SlabReferenceTable({ lang, year }: { lang: "en" | "bn"; year: ReturnType<typeof getTaxYear> }) {
  const cats: AssesseeCategory[] = ["male", "female", "senior", "disabled", "freedomFighter"];
  const num = (v: number | string) => (lang === "bn" ? toBengaliNumerals(v) : String(v));
  return (
    <div className="overflow-x-auto rounded-xl border border-border">
      <table className="w-full text-sm">
        <thead className="bg-muted/60 text-xs uppercase tracking-wide text-muted-foreground">
          <tr>
            <th className={cn("px-3 py-2 text-left", lang === "bn" && "font-bn")}>{lang === "en" ? "Category" : "শ্রেণি"}</th>
            <th className={cn("px-3 py-2 text-right", lang === "bn" && "font-bn")}>{lang === "en" ? "Exemption" : "করমুক্ত সীমা"}</th>
            <th className={cn("px-3 py-2 text-left", lang === "bn" && "font-bn")}>{lang === "en" ? "Slab structure (above exemption)" : "স্ল্যাব (করমুক্ত সীমার উপরে)"}</th>
          </tr>
        </thead>
        <tbody>
          {cats.map((c) => {
            const ex = getExemption(year, c);
            return (
              <tr key={c} className="border-t border-border align-top">
                <td className={cn("px-3 py-2", lang === "bn" && "font-bn")}>{CATEGORY_LABEL[c][lang]}</td>
                <td className="px-3 py-2 text-right tabular-nums">{formatBDT(ex, lang, 0)}</td>
                <td className="px-3 py-2 text-xs text-muted-foreground">
                  {year.slabs.map((s, i) => (
                    <span key={i} className="mr-2 inline-block">
                      {isFinite(s.amount)
                        ? `${formatBDT(s.amount, lang, 0)} @ ${num((s.rate * 100).toFixed(0))}%`
                        : `${lang === "en" ? "above" : "তদূর্ধ্ব"} @ ${num((s.rate * 100).toFixed(0))}%`}
                      {i < year.slabs.length - 1 ? "," : ""}
                    </span>
                  ))}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
